import { CheckCircle, XCircle, Hash, Shield, Clock, FileText, Lock, Eye } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";

export default function ReceiptDisplay({ receipt, publicOnly = false }) {
  if (!receipt) return null;

  const isApproved = receipt.outcome === "approved";
  const isSigned = receipt.verification_status === "signed" || receipt.katlas_signed;
  const car = receipt.car_block || receipt.car_summary || {};

  return (
    <div data-testid="receipt-display" className="border border-zinc-800 bg-zinc-900 p-6 space-y-5">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Badge className="rounded-none text-[9px] font-mono bg-blue-500/10 text-blue-400 border-blue-500/20">
              KATLAS RECEIPT
            </Badge>
            {isSigned ? (
              <Badge className="rounded-none text-[9px] font-mono bg-emerald-500/10 text-emerald-400 border-emerald-500/20">
                SIGNED
              </Badge>
            ) : (
              <Badge className="rounded-none text-[9px] font-mono bg-amber-500/10 text-amber-400 border-amber-500/20">
                UNSIGNED
              </Badge>
            )}
          </div>
          <h3 className="text-base font-medium text-zinc-50">
            Governed Review {isApproved ? "Approved" : "Refused"}
          </h3>
          <span className="text-[10px] font-mono text-zinc-500">
            {receipt.event_id} | {receipt.system_name}
          </span>
        </div>
        <div className="flex items-center gap-2">
          {isApproved ? (
            <CheckCircle className="h-6 w-6 text-emerald-400" strokeWidth={1.5} />
          ) : (
            <XCircle className="h-6 w-6 text-red-400" strokeWidth={1.5} />
          )}
        </div>
      </div>

      <Separator className="bg-zinc-800" />

      {/* Key fields grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="Outcome" value={receipt.outcome?.toUpperCase()} icon={isApproved ? CheckCircle : XCircle} color={isApproved ? "text-emerald-400" : "text-red-400"} />
        <Field label="Acting Role" value={receipt.actor_role} icon={Shield} />
        <Field label="Local Timestamp" value={formatTs(receipt.local_timestamp)} icon={Clock} />
        <Field label="KATLAS Node Timestamp" value={receipt.katlas_node_timestamp ? formatTs(receipt.katlas_node_timestamp) : "Not available"} icon={Clock} />
        <Field label="Verification Status" value={receipt.verification_status?.toUpperCase()} icon={isSigned ? CheckCircle : Lock} color={isSigned ? "text-emerald-400" : "text-amber-400"} />
        <Field label="Signer / Node Reference" value={receipt.signer_reference || "Not available"} icon={Shield} mono />
      </div>

      <Separator className="bg-zinc-800" />

      {/* Receipt Hash */}
      <div className="border border-zinc-800 bg-zinc-950 p-3">
        <div className="flex items-center gap-2 mb-1">
          <Hash className="h-3.5 w-3.5 text-zinc-500" strokeWidth={1.5} />
          <span className="text-[10px] uppercase tracking-widest text-zinc-500">Receipt Hash</span>
        </div>
        <div className="evidence-hash" data-testid="receipt-hash">{receipt.hash}</div>
      </div>

      {receipt.attestation_reference && (
        <div className="border border-zinc-800 bg-zinc-950 p-3">
          <div className="flex items-center gap-2 mb-1">
            <FileText className="h-3.5 w-3.5 text-zinc-500" strokeWidth={1.5} />
            <span className="text-[10px] uppercase tracking-widest text-zinc-500">Attestation Reference</span>
          </div>
          <div className="evidence-hash">{receipt.attestation_reference}</div>
        </div>
      )}

      <Separator className="bg-zinc-800" />

      {/* Shared Evidence Refs */}
      <div>
        <div className="flex items-center gap-2 mb-2">
          <Eye className="h-3.5 w-3.5 text-emerald-400" strokeWidth={1.5} />
          <span className="text-[10px] uppercase tracking-widest text-zinc-500">
            Shared Evidence References ({receipt.shared_evidence_refs?.length || 0})
          </span>
        </div>
        <div className="space-y-1">
          {receipt.shared_evidence_refs?.map((ref, i) => (
            <div key={i} className="flex items-center gap-2 text-[11px] font-mono py-1 px-2 bg-zinc-950">
              <span className="text-blue-400">{ref.ref}</span>
              <span className="text-zinc-400">{ref.title || ref.category}</span>
            </div>
          ))}
        </div>
        <div className="mt-2 text-[11px] font-mono text-amber-400/70">
          Withheld evidence count: {receipt.withheld_evidence_count}
        </div>
      </div>

      <Separator className="bg-zinc-800" />

      {/* CAR Block */}
      <div>
        <span className="text-[10px] uppercase tracking-widest text-zinc-500 block mb-3">
          CAR Block
        </span>
        <div className="space-y-2">
          <CARRow label="Custody" value={car.custody} color="text-blue-400" />
          <CARRow label="Authority" value={car.authority} color="text-amber-400" />
          <CARRow label="Receipts" value={car.receipts} color="text-emerald-400" />
        </div>
      </div>

      <Separator className="bg-zinc-800" />

      {/* Data Boundary */}
      <div className="border border-zinc-800 bg-zinc-950 p-3">
        <span className="text-[10px] uppercase tracking-widest text-zinc-600 block mb-1">Data Boundary Statement</span>
        <p className="text-xs text-zinc-400 leading-relaxed">
          {receipt.data_boundary_statement}
        </p>
      </div>

      {/* KATLAS error if unsigned */}
      {!publicOnly && receipt.katlas_error && (
        <div className="border border-amber-500/20 bg-amber-500/5 p-3">
          <span className="text-[10px] uppercase tracking-widest text-amber-500/70 block mb-1">KATLAS Note</span>
          <p className="text-xs text-amber-400/70 font-mono">{receipt.katlas_error}</p>
        </div>
      )}

      {/* Public receipt link */}
      {!publicOnly && receipt.hash && (
        <div className="text-[11px] font-mono text-zinc-600">
          Public receipt: <a href={`/r/${receipt.hash}`} className="text-blue-400 hover:text-blue-300 underline">/r/{receipt.hash.slice(0, 16)}...</a>
        </div>
      )}
    </div>
  );
}

function Field({ label, value, icon: Icon, color = "text-zinc-200", mono = false }) {
  return (
    <div className="border border-zinc-800 bg-zinc-950 p-2.5">
      <span className="text-[10px] uppercase tracking-widest text-zinc-600 block mb-0.5">{label}</span>
      <div className="flex items-center gap-1.5">
        {Icon && <Icon className={`h-3.5 w-3.5 ${color}`} strokeWidth={1.5} />}
        <span className={`text-sm ${color} ${mono ? "font-mono text-xs" : ""}`}>{value || "\u2014"}</span>
      </div>
    </div>
  );
}

function CARRow({ label, value, color }) {
  return (
    <div className="border border-zinc-800 bg-zinc-950 p-2.5">
      <span className={`text-[10px] uppercase tracking-widest ${color} font-semibold block mb-0.5`}>{label}</span>
      <p className="text-xs text-zinc-400 leading-relaxed">{value}</p>
    </div>
  );
}

function formatTs(ts) {
  if (!ts) return "\u2014";
  try { return new Date(ts).toLocaleString(); } catch { return ts; }
}
